import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Building2, Check } from 'lucide-react';
import { useAppStore } from '@/stores/useAppStore';
import type { Entity } from '@/types';

interface EntitySwitcherProps {
  entities: Entity[];
}

export function EntitySwitcher({ entities }: EntitySwitcherProps) {
  const { activeEntity, displayMode, switchEntity } = useAppStore();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const hidden = displayMode === 'simple' || entities.length <= 1;

  useEffect(() => {
    if (hidden) return;
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [hidden]);

  if (hidden) return null;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex min-w-0 items-center gap-2 px-2 sm:px-3 py-2 rounded-[var(--radius-md)] hover:bg-primary-50 dark:hover:bg-slate-700 transition-colors"
        aria-label="Switch entity"
      >
        <Building2 className="w-4 h-4 shrink-0 text-primary-600" />
        {/* Hard truncation on mobile keeps the topbar within 375px regardless of
            entity-name length (long demo names + nav badges previously overflowed
            the header right cluster by ~50px — caught by the mobile-overflow gate). */}
        <span className="text-sm font-medium text-slate-700 dark:text-slate-200 max-w-[88px] sm:max-w-[180px] min-w-0 truncate">
          {activeEntity?.legalName ?? 'Select Entity'}
        </span>
        <ChevronDown className={`w-4 h-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-white dark:bg-slate-800 rounded-[var(--radius-lg)] shadow-lg border border-slate-200 dark:border-slate-700 z-50 py-1">
          {entities.map((entity) => (
            <button
              key={entity.id}
              onClick={() => {
                switchEntity(entity.id);
                setOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-left"
            >
              <Building2 className="w-4 h-4 text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                  {entity.legalName}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {entity.taxElection.toUpperCase()} &middot; {entity.compensationModel.replace('_', ' ')}
                </p>
              </div>
              {activeEntity?.id === entity.id && (
                <Check className="w-4 h-4 text-primary-600 shrink-0" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
